// API Status Monitor for PadelMaster Console
// Real-time monitoring of all backend APIs with visual indicators

import { getApiUrl } from '../config/domains';
import { lerAccessToken } from '@/security/tokens';

export interface ApiEndpoint {
  name: string;
  url: string;
  method: 'GET' | 'POST';
  timeout: number;
  critical: boolean;
  category: string;
}

export interface ApiStatus {
  name: string;
  status: 'online' | 'offline' | 'checking' | 'error';
  responseTime: number;
  lastChecked: Date;
  error?: string;
  category: string;
}

export const API_ENDPOINTS: ApiEndpoint[] = [
  // Dashboard APIs
  {
    name: 'Dashboard Games',
    url: '/padel/games/dashboard/games/',
    method: 'GET',
    timeout: 5000,
    critical: true,
    category: 'Dashboard'
  },
  {
    name: 'Dashboard Save',
    url: '/padel/games/dashboard/save/',
    method: 'POST',
    timeout: 8000,
    critical: true,
    category: 'Dashboard'
  },
  
  // Game Systems APIs
  {
    name: '8 Players Save',
    url: '/padel/games/8players/saveGame/',
    method: 'POST',
    timeout: 8000,
    critical: true,
    category: '8 Players'
  },
  {
    name: '8 Players Load',
    url: '/padel/games/8players/loadGame/',
    method: 'GET',
    timeout: 5000,
    critical: true,
    category: '8 Players'
  },
  {
    name: '12 Players Save',
    url: '/padel/games/12players/saveGame/',
    method: 'POST',
    timeout: 8000,
    critical: true,
    category: '12 Players'
  },
  {
    name: '12 Players Load',
    url: '/padel/games/12players/loadGame/',
    method: 'GET',
    timeout: 5000,
    critical: true,
    category: '12 Players'
  },
  {
    name: '16 Players Save',
    url: '/padel/games/16players/saveGame/',
    method: 'POST',
    timeout: 8000,
    critical: false, // Not yet implemented
    category: '16 Players'
  },
  {
    name: '16 Players Load',
    url: '/padel/games/16players/loadGame/',
    method: 'GET',
    timeout: 5000,
    critical: false, // Not yet implemented
    category: '16 Players'
  },
  
  // Authentication APIs
  {
    name: 'Token Refresh',
    url: '/auth/token/refresh/',
    method: 'POST',
    timeout: 5000,
    critical: true,
    category: 'Auth'
  },
  {
    name: 'User Profile',
    url: '/auth/user/profile/',
    method: 'GET',
    timeout: 5000,
    critical: true,
    category: 'Auth'
  },
  
  // Tournament APIs
  {
    name: 'Tournament List',
    url: '/padel/tournaments/',
    method: 'GET',
    timeout: 5000,
    critical: false,
    category: 'Tournaments'
  },
  {
    name: 'Tournament Create',
    url: '/padel/tournaments/create/',
    method: 'POST',
    timeout: 8000,
    critical: false,
    category: 'Tournaments'
  },
  
  // Club/Venue APIs
  {
    name: 'Clubs List',
    url: '/padel/clubs/',
    method: 'GET',
    timeout: 5000,
    critical: false,
    category: 'Clubs'
  },
  {
    name: 'Venues List',
    url: '/padel/venues/',
    method: 'GET',
    timeout: 5000,
    critical: false,
    category: 'Venues'
  },
  
  // General Backend Status
  {
    name: 'Backend Status',
    url: '/padel/get-all-counts/',
    method: 'GET',
    timeout: 3000,
    critical: true,
    category: 'System'
  },
  {
    name: 'API Status',
    url: '/api/status/',
    method: 'GET',
    timeout: 3000,
    critical: true,
    category: 'System'
  }
];

class ApiStatusMonitor {
  private statusMap: Map<string, ApiStatus> = new Map();
  private isMonitoring: boolean = false;
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiUrl();
  }

  async checkSingleApi(endpoint: ApiEndpoint): Promise<ApiStatus> {
    const startTime = Date.now();
    const status: ApiStatus = {
      name: endpoint.name,
      status: 'checking',
      responseTime: 0,
      lastChecked: new Date(),
      category: endpoint.category
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), endpoint.timeout);

      const response = await fetch(`${this.baseUrl}${endpoint.url}`, {
        method: endpoint.method,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${lerAccessToken() || ''}`
        },
        ...(endpoint.method === 'POST' && {
          body: JSON.stringify({
            test: true,
            source: 'api_monitor'
          })
        })
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        status.status = 'online';
        status.responseTime = responseTime;
      } else {
        status.status = 'error';
        status.responseTime = responseTime;
        status.error = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      status.status = 'offline';
      status.responseTime = responseTime;
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          status.error = `Timeout after ${endpoint.timeout}ms`;
        } else {
          status.error = error.message;
        }
      } else {
        status.error = 'Unknown error';
      }
    }

    this.statusMap.set(endpoint.name, status);
    return status;
  }

  async checkAllApis(): Promise<Map<string, ApiStatus>> {
    console.log('%c🔍 Verificando status de todas as APIs...', 'color: #1e40af; font-weight: bold;');
    
    // Check critical APIs first
    const criticalApis = API_ENDPOINTS.filter(api => api.critical);
    const nonCriticalApis = API_ENDPOINTS.filter(api => !api.critical);

    // Run critical checks in parallel
    const criticalPromises = criticalApis.map(api => this.checkSingleApi(api));
    await Promise.all(criticalPromises);

    // Run non-critical checks in parallel (but separate from critical)
    const nonCriticalPromises = nonCriticalApis.map(api => this.checkSingleApi(api));
    await Promise.all(nonCriticalPromises);

    return this.statusMap;
  }

  displayApiStatus(): void {
    console.log('\n%c🌐 STATUS DAS APIs DO BACKEND', 'color: #1e40af; font-weight: bold; font-size: 16px; background: #dbeafe; padding: 8px; border-radius: 4px;');
    
    // Group by category
    const categories = [...new Set(API_ENDPOINTS.map(api => api.category))];
    
    categories.forEach(category => {
      const categoryApis = API_ENDPOINTS.filter(api => api.category === category);
      const categoryStatuses = categoryApis.map(api => this.statusMap.get(api.name)).filter(Boolean);
      
      // Calculate category health
      const onlineCount = categoryStatuses.filter(s => s?.status === 'online').length;
      const totalCount = categoryStatuses.length;
      const healthPercentage = totalCount > 0 ? Math.round((onlineCount / totalCount) * 100) : 0;
      
      // Category header with health indicator
      let categoryIcon = '🔴';
      if (healthPercentage === 100) categoryIcon = '🟢';
      else if (healthPercentage >= 50) categoryIcon = '🟡';
      
      console.log(`\n%c${categoryIcon} ${category} (${onlineCount}/${totalCount} - ${healthPercentage}%)`, 
        `color: ${healthPercentage === 100 ? '#16a34a' : healthPercentage >= 50 ? '#ea580c' : '#dc2626'}; font-weight: bold; font-size: 14px;`);
      
      // Individual API status
      categoryApis.forEach(api => {
        const status = this.statusMap.get(api.name);
        if (!status) return;
        
        const icon = this.getStatusIcon(status.status);
        const responseTime = status.responseTime > 0 ? ` (${status.responseTime}ms)` : '';
        const critical = api.critical ? ' [CRÍTICA]' : '';
        
        console.log(`%c   ${icon} ${api.name}${critical}${responseTime}`, 
          `color: ${this.getStatusColor(status.status)}; font-size: 12px;`);
        
        if (status.error) {
          console.log(`%c      └─ Erro: ${status.error}`, 'color: #991b1b; font-size: 11px;');
        }
      });
    });

    // Overall system health
    this.displayOverallHealth();
  }

  private getStatusIcon(status: string): string {
    switch (status) {
      case 'online': return '🟢';
      case 'offline': return '🔴';
      case 'error': return '🟠';
      case 'checking': return '🟡';
      default: return '⚪';
    }
  }

  private getStatusColor(status: string): string {
    switch (status) {
      case 'online': return '#16a34a';
      case 'offline': return '#dc2626';
      case 'error': return '#ea580c';
      case 'checking': return '#eab308';
      default: return '#6b7280';
    }
  }

  private displayOverallHealth(): void {
    const allStatuses = Array.from(this.statusMap.values());
    const criticalStatuses = allStatuses.filter(s => {
      const endpoint = API_ENDPOINTS.find(api => api.name === s.name);
      return endpoint?.critical;
    });

    const onlineCount = allStatuses.filter(s => s.status === 'online').length;
    const criticalOnlineCount = criticalStatuses.filter(s => s.status === 'online').length;
    const totalCount = allStatuses.length;
    const criticalTotalCount = criticalStatuses.length;

    const overallHealth = totalCount > 0 ? Math.round((onlineCount / totalCount) * 100) : 0;
    const criticalHealth = criticalTotalCount > 0 ? Math.round((criticalOnlineCount / criticalTotalCount) * 100) : 0;

    console.log('\n%c📊 RESUMO GERAL', 'color: #7c3aed; font-weight: bold; font-size: 14px;');
    console.log(`%c   ├─ APIs Críticas: ${criticalOnlineCount}/${criticalTotalCount} (${criticalHealth}%)`, 
      `color: ${criticalHealth === 100 ? '#16a34a' : '#dc2626'}; font-size: 12px;`);
    console.log(`%c   ├─ Total de APIs: ${onlineCount}/${totalCount} (${overallHealth}%)`, 
      `color: ${overallHealth >= 80 ? '#16a34a' : overallHealth >= 50 ? '#ea580c' : '#dc2626'}; font-size: 12px;`);

    // System status
    let systemStatus = 'CRÍTICO';
    let systemColor = '#dc2626';
    
    if (criticalHealth === 100) {
      systemStatus = 'EXCELENTE';
      systemColor = '#16a34a';
    } else if (criticalHealth >= 80) {
      systemStatus = 'BOM';
      systemColor = '#059669';
    } else if (criticalHealth >= 50) {
      systemStatus = 'DEGRADADO';
      systemColor = '#ea580c';
    }

    console.log(`%c   └─ Status do Sistema: ${systemStatus}`, `color: ${systemColor}; font-weight: bold; font-size: 12px;`);

    // Performance insights
    const avgResponseTime = allStatuses
      .filter(s => s.status === 'online' && s.responseTime > 0)
      .reduce((sum, s) => sum + s.responseTime, 0) / 
      allStatuses.filter(s => s.status === 'online' && s.responseTime > 0).length;

    if (avgResponseTime > 0) {
      console.log(`%c⚡ Tempo médio de resposta: ${Math.round(avgResponseTime)}ms`, 
        `color: ${avgResponseTime < 500 ? '#16a34a' : avgResponseTime < 1000 ? '#ea580c' : '#dc2626'}; font-size: 12px;`);
    }
  }

  async startMonitoring(intervalSeconds: number = 30): Promise<void> {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    console.log(`%c🔄 Monitoramento automático iniciado (${intervalSeconds}s)`, 'color: #059669; font-weight: bold;');
    
    // Initial check
    await this.checkAllApis();
    this.displayApiStatus();

    // Set up interval
    setInterval(async () => {
      if (this.isMonitoring) {
        await this.checkAllApis();
        console.log(`%c🔄 APIs verificadas às ${new Date().toLocaleTimeString()}`, 'color: #6b7280; font-size: 11px;');
      }
    }, intervalSeconds * 1000);
  }

  stopMonitoring(): void {
    this.isMonitoring = false;
    console.log('%c⏹️ Monitoramento automático parado', 'color: #ea580c; font-weight: bold;');
  }

  getSystemHealth(): {
    overall: number;
    critical: number;
    status: string;
    details: Map<string, ApiStatus>;
  } {
    const allStatuses = Array.from(this.statusMap.values());
    const criticalStatuses = allStatuses.filter(s => {
      const endpoint = API_ENDPOINTS.find(api => api.name === s.name);
      return endpoint?.critical;
    });

    const onlineCount = allStatuses.filter(s => s.status === 'online').length;
    const criticalOnlineCount = criticalStatuses.filter(s => s.status === 'online').length;
    const totalCount = allStatuses.length;
    const criticalTotalCount = criticalStatuses.length;

    const overallHealth = totalCount > 0 ? Math.round((onlineCount / totalCount) * 100) : 0;
    const criticalHealth = criticalTotalCount > 0 ? Math.round((criticalOnlineCount / criticalTotalCount) * 100) : 0;

    let status = 'CRÍTICO';
    if (criticalHealth === 100) status = 'EXCELENTE';
    else if (criticalHealth >= 80) status = 'BOM';
    else if (criticalHealth >= 50) status = 'DEGRADADO';

    return {
      overall: overallHealth,
      critical: criticalHealth,
      status,
      details: this.statusMap
    };
  }
}

// Export singleton instance
export const apiMonitor = new ApiStatusMonitor();

// Quick check function for console use
export const checkApiStatus = async (): Promise<void> => {
  await apiMonitor.checkAllApis();
  apiMonitor.displayApiStatus();
};

// Auto-start monitoring function
export const startApiMonitoring = (intervalSeconds: number = 30): void => {
  apiMonitor.startMonitoring(intervalSeconds);
};
